// Multi Lead Form spec §2. Pure helpers powering the Campaign editor's
// tab strip: add / rename / delete / set-as-default. Kept free of React
// + Firestore so they can be unit-tested against fixtures.
//
// Invariants enforced (matching server-side service.ts#assertLeadFormsValid):
//
//   - At least one form per Campaign (spec §2.3: "Can't delete the last
//     form").
//   - Name uniqueness within the array, case-insensitive after trim
//     (matches the server's check + the tab strip's display comparison).
//   - defaultLeadFormId always points to an entry that exists.
//   - Deleting the default form is BLOCKED — AM must reassign default
//     first (spec §2.3).
//
// Helpers return either `{ ok: true, forms, defaultLeadFormId }` or
// `{ ok: false, error }`; consumers branch on that union for the surface
// they need (toast, inline error, no-op).

import type { CampaignLeadForm } from '../types/campaign';
import type { FormType } from '../types/franchiseLocationProfile';

export type LeadFormOpResult =
  | { ok: true; forms: CampaignLeadForm[]; defaultLeadFormId: string }
  | { ok: false; error: string };

// Stable id generator with a coarse fallback for old runtimes. Same
// pattern as ColumnPresetMenu / server service.
export function generateLeadFormId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `lf-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// Case-insensitive name lookup, ignoring a specific id (the entry being
// renamed). Returns true if `name` collides with another entry.
function nameTaken(
  forms: CampaignLeadForm[],
  name: string,
  ignoreId: string | null,
): boolean {
  const lowered = name.trim().toLowerCase();
  return forms.some((f) => f.id !== ignoreId && f.name.trim().toLowerCase() === lowered);
}

export function addLeadForm(
  forms: CampaignLeadForm[],
  defaultLeadFormId: string,
  name: string,
): LeadFormOpResult {
  const trimmed = name.trim();
  if (trimmed.length === 0) {
    return { ok: false, error: 'Lead form name is required.' };
  }
  if (nameTaken(forms, trimmed, null)) {
    return { ok: false, error: `A lead form named "${trimmed}" already exists.` };
  }
  const next: CampaignLeadForm = {
    id: generateLeadFormId(),
    name: trimmed,
    formType: 'Generic',
    fields: {
      headline: { mode: 'Generic' },
      description: { mode: 'Generic' },
      thankYouHeadline: { mode: 'Generic' },
      thankYouDescription: { mode: 'Generic' },
    },
  };
  return { ok: true, forms: [...forms, next], defaultLeadFormId };
}

export function renameLeadForm(
  forms: CampaignLeadForm[],
  defaultLeadFormId: string,
  id: string,
  name: string,
): LeadFormOpResult {
  const trimmed = name.trim();
  if (trimmed.length === 0) {
    return { ok: false, error: 'Lead form name is required.' };
  }
  if (nameTaken(forms, trimmed, id)) {
    return { ok: false, error: `A lead form named "${trimmed}" already exists.` };
  }
  const next = forms.map((f) => (f.id === id ? { ...f, name: trimmed } : f));
  return { ok: true, forms: next, defaultLeadFormId };
}

export function deleteLeadForm(
  forms: CampaignLeadForm[],
  defaultLeadFormId: string,
  id: string,
): LeadFormOpResult {
  if (forms.length <= 1) {
    return { ok: false, error: 'Campaign must have at least one lead form.' };
  }
  if (id === defaultLeadFormId) {
    return {
      ok: false,
      error: 'Set a different default lead form before deleting this one.',
    };
  }
  const next = forms.filter((f) => f.id !== id);
  return { ok: true, forms: next, defaultLeadFormId };
}

export function setDefaultLeadForm(
  forms: CampaignLeadForm[],
  _defaultLeadFormId: string,
  id: string,
): LeadFormOpResult {
  if (!forms.some((f) => f.id === id)) {
    return { ok: false, error: 'Cannot set default to a lead form that does not exist.' };
  }
  return { ok: true, forms, defaultLeadFormId: id };
}

// Update a single entry's authored content (fields + formType). Pure
// pass-through with the array immutability rule applied; no validation
// beyond the entry's existence.
export function patchLeadForm(
  forms: CampaignLeadForm[],
  id: string,
  patch: Partial<Pick<CampaignLeadForm, 'formType' | 'fields'>>,
): CampaignLeadForm[] {
  return forms.map((f) =>
    f.id === id
      ? {
          ...f,
          ...(patch.formType !== undefined ? { formType: patch.formType as FormType } : {}),
          ...(patch.fields !== undefined ? { fields: patch.fields } : {}),
        }
      : f,
  );
}
